import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import Loading from '../components/Loading';
import ProductionLane from '../components/production/ProductionLane';
import { useAuth } from '../AuthenticatedApp';
import { useVenueFeedback } from '../context/VenueFeedbackContext';
import { useVenueId } from '../hooks/useUserData';
import { supabase } from '../supabaseClient';
import {
  getVenueProductionQueue,
  updateOrderItemStatus,
} from '../utils/staffApiService';
import '../styles/OrdersPage.css';

  const OrdersPage = () => {
    const venueId = useVenueId();
    const { getAccessTokenSilently } = useAuth0();
    const { userPermissions = [] } = useAuth();
  const { pushFeedback } = useVenueFeedback();
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [activeItemId, setActiveItemId] = useState(null);

  const hasProductionAccess = userPermissions.includes('manage_shift');

  const loadQueue = useCallback(async () => {
    if (!venueId || !hasProductionAccess) {
      setQueueItems([]);
      setPageError('');
      setLoading(false);
      return;
    }

    try {
      const response = await getVenueProductionQueue(venueId, getAccessTokenSilently);
      setQueueItems(response?.items || []);
      setPageError('');
    } catch (error) {
      setQueueItems([]);
      setPageError(error?.message || 'Uretim kuyrugu yuklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, hasProductionAccess, venueId]);

  useEffect(() => {
    setLoading(true);
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (!venueId || !hasProductionAccess) {
      return undefined;
    }

    const refetch = () => {
      loadQueue();
    };

    const orderItemsChannel = supabase
      .channel(`venue-production-order-items:${venueId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, refetch)
      .subscribe();

    const ordersChannel = supabase
      .channel(`venue-production-orders:${venueId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, refetch)
      .subscribe();

    return () => {
      supabase.removeChannel(orderItemsChannel);
      supabase.removeChannel(ordersChannel);
    };
  }, [hasProductionAccess, loadQueue, venueId]);

  const handleTransition = useCallback(async (orderItemId, nextStatus) => {
    setActiveItemId(orderItemId);
    try {
      await updateOrderItemStatus(venueId, orderItemId, nextStatus, getAccessTokenSilently);
      await loadQueue();
    } catch (error) {
      pushFeedback({
        type: 'error',
        title: 'Urun durumu guncellenemedi',
        message: error?.message || 'Uretim durumu kaydedilemedi.',
      });
    } finally {
      setActiveItemId(null);
    }
  }, [getAccessTokenSilently, loadQueue, pushFeedback, venueId]);

  const pendingItems = useMemo(
    () => queueItems.filter((item) => item.status === 'pending'),
    [queueItems]
  );
  const preparingItems = useMemo(
    () => queueItems.filter((item) => item.status === 'preparing'),
    [queueItems]
  );
  const completedItems = useMemo(
    () => queueItems.filter((item) => item.status === 'completed'),
    [queueItems]
  );

  if (!hasProductionAccess) {
    return (
      <div className="venue-page">
        <section className="venue-surface-card venue-surface-card--padded venue-empty-state venue-empty-state--compact">
          <div className="venue-page-eyebrow">Siparişler</div>
          <h2 className="venue-page-title">Bu ekran yalnızca mutfak ve bar ekibi içindir</h2>
          <p className="venue-page-subtitle">
            Bu hesabın Siparişler ekranına girebilmesi için <code>manage_shift</code> yetkisi gerekir.
          </p>
        </section>
      </div>
    );
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="venue-page">
      <section className="venue-surface-card venue-surface-card--padded venue-production-surface">
        <div className="venue-production-board">
          <ProductionLane
            title="Yeni"
            items={pendingItems}
            actionLabel="Başla"
            busyItemId={activeItemId}
            onAction={(orderItemId) => handleTransition(orderItemId, 'preparing')}
          />
          <ProductionLane
            title="Hazırlanıyor"
            items={preparingItems}
            actionLabel="Tamamla"
            busyItemId={activeItemId}
            onAction={(orderItemId) => handleTransition(orderItemId, 'completed')}
          />
          <ProductionLane
            title="Tamamlandı"
            items={completedItems}
            emptyMessage="Tamamlanan ürün yok."
          />
        </div>
        {pageError ? (
          <p className="venue-production-error" role="alert">
            {pageError}
          </p>
        ) : null}
      </section>
    </div>
  );
};

export default OrdersPage;
